import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ImageViewer from './ImageViewer';
import ARGlbViewer from './ARGlbViewer';
import { getGLBPath } from '../utils/fileUtils';
import firebaseService from '../firebase.service';
import { useRedirect } from '../contexts/RedirectContext';
import { useDataCache } from '../contexts/DataCacheContext';
import { useProject } from '../contexts/ProjectContext';
import optimizedDataService from '../services/OptimizedDataService';
import * as XLSX from 'xlsx';
import volume from './volume.png';

const CSVBookDetail = () => {
  const { projectId, bookid, companyid } = useParams();
  const location = useLocation();
  const { isValidProject, defaultProject, projects } = useProject();
  const viewerRef = useRef(null);
  const [bookData, setBookData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mediaType, setMediaType] = useState('none');
  const [activeTab, setActiveTab] = useState('description');
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  
  const project = (projectId && isValidProject(projectId)) ? projectId : defaultProject;
  const currentProject = projects.find(p => p.id === project);
  const logoUrl = currentProject?.logoUrl || '/hong-kong-pavilion.png';
  const logoAlt = currentProject?.name || 'Book Fair';
  const { t, i18n } = useTranslation();
  
  // Force language change immediately based on URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const urlLang = urlParams.get('lang');
  if (urlLang && ['tc', 'sc', 'en'].includes(urlLang) && i18n.language !== urlLang) {
    i18n.changeLanguage(urlLang);
  }
  
  const { language: lang } = i18n;

  // Projects that use pre-generated audio files stored in Firebase Storage.
  // All other projects fall back to calling the Google TTS API on demand.
  const PREGENERATED_AUDIO_PROJECTS = ['bcbf2026'];
  const usePreGeneratedAudio = PREGENERATED_AUDIO_PROJECTS.includes(project);

  // Legacy TTS configuration (used for fbf2025, fbf2024, etc.)
  const legacyApiUrl = `https://texttospeech.googleapis.com/v1/text:synthesize?key=AIzaSyCViaX449bDDIDs4D2qlC4N5QOvH3NRL3Q`;
  const langSpeech = {
    en: { languageCode: 'en-US', name: 'en-US-Standard-H', pitch: -0.2, speakingRate: 0.9 },
    tc: { languageCode: 'yue-HK', name: 'yue-HK-Standard-A', pitch: -0.2, speakingRate: 0.95 },
    sc: { languageCode: 'cmn-CN', name: 'cmn-CN-Standard-D', pitch: -0.2, speakingRate: 0.9 },
  };

  const base64ToBlob = (base64String, mimeType) => {
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  };

  const toggleAudio = (text) => {
    const audioPlayer = document.getElementById('audioPlayer');
    if (!audioPlayer) return;

    if (usePreGeneratedAudio) {
      // New path: play from pre-generated Firebase Storage file
      if (!audioUrl) return;
      if (isAudioPlaying) {
        audioPlayer.pause();
        setIsAudioPlaying(false);
      } else {
        if (audioPlayer.src !== audioUrl) audioPlayer.src = audioUrl;
        audioPlayer.play();
        setIsAudioPlaying(true);
      }
    } else {
      // Legacy path: call Google TTS API on demand
      if (!text || !text.trim()) return;
      if (isAudioPlaying) {
        audioPlayer.pause();
        setIsAudioPlaying(false);
      } else {
        if (audioPlayer.src && audioPlayer.src !== window.location.href) {
          audioPlayer.play();
          setIsAudioPlaying(true);
        } else {
          fetch(legacyApiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              input: { text },
              voice: { languageCode: langSpeech[lang].languageCode, ssmlGender: 'FEMALE', name: langSpeech[lang].name },
              audioConfig: { audioEncoding: 'MP3', pitch: langSpeech[lang].pitch, speakingRate: langSpeech[lang].speakingRate },
            }),
          })
            .then(r => r.json())
            .then(data => {
              if (!data.audioContent) return;
              const blob = base64ToBlob(data.audioContent, 'audio/mpeg');
              audioPlayer.src = URL.createObjectURL(blob);
              audioPlayer.play();
              setIsAudioPlaying(true);
            })
            .catch(() => {});
        }
      }
    }
  };

  // Handle audio end event
  useEffect(() => {
    const audioPlayer = document.getElementById('audioPlayer');
    if (audioPlayer) {
      const handleAudioEnd = () => setIsAudioPlaying(false);
      audioPlayer.addEventListener('ended', handleAudioEnd);
      return () => audioPlayer.removeEventListener('ended', handleAudioEnd);
    }
  }, []);

  // Load pre-generated audio URL from Firebase Storage (BCBF2026 only)
  useEffect(() => {
    if (!usePreGeneratedAudio) {
      setAudioUrl(null);
      return;
    }
    if (!bookData?.code) {
      setAudioUrl(null);
      return;
    }
    const audioPath = `${project.toUpperCase()}/audio/${bookData.code}_${lang}.mp3`;
    firebaseService.getFileUrl(audioPath).then((url) => {
      setAudioUrl(url);
      const audioPlayer = document.getElementById('audioPlayer');
      if (audioPlayer) {
        audioPlayer.pause();
        audioPlayer.src = '';
        setIsAudioPlaying(false);
      }
    });
  }, [bookData?.code, lang, project, usePreGeneratedAudio]);

  const { getRedirectUrl, setRedirectLinksFromData, isRedirectEnabled, getRedirectLinks, isMobileDevice } = useRedirect();
  const dataCache = useDataCache();

  // Initialize optimized data service with cache context
  useEffect(() => {
    optimizedDataService.setCache(dataCache);
  }, [dataCache]);

  // Add language class to body for CSS targeting
  useEffect(() => {
    document.body.className = document.body.className.replace(/lang-\w+/g, '');
    document.body.classList.add(`lang-${lang}`);
    
    return () => {
      document.body.classList.remove(`lang-${lang}`);
    };
  }, [lang]);


  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (companyid) {
          // Fetch company data using optimized service
          const company = await optimizedDataService.getCompany(project, companyid);
          
          if (company) {
            setCompanyData(company);
          } else {
            setError('Company not found');
          }
        } else if (bookid) {
          // Fetch book data using optimized service only
          const book = await optimizedDataService.getBook(project, bookid);
          
          if (book) {
            setBookData(book);
            
            // Use stored media type from Excel data (detected during upload)
            
            if (book.mediaType) {
              setMediaType(book.mediaType);
            } else {
              // Default to 'none' if no media type is defined
              setMediaType('none');
            }
            
            // Special case: Force AR viewer for FB-P-CC-001 for testing
            if (bookid === "FB-P-CC-001") {
              setMediaType('glb');
            }
          } else {
            setError(`Book not found. Searched for code: ${bookid}`);
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Error loading data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [bookid, companyid, project]);

  const redirectEnabled = isRedirectEnabled(project);
  const redirectLinks = getRedirectLinks(project);

  // Set redirect links from book data when loaded
  useEffect(() => {
    if (bookData) {
      setRedirectLinksFromData(project, [bookData]);
    }
  }, [bookData, project, setRedirectLinksFromData]);

  // Load all redirect links using optimized service
  useEffect(() => {
    const loadAllRedirectLinks = async () => {
      try {
        const books = await optimizedDataService.getAllBooks(project);
        setRedirectLinksFromData(project, books);
      } catch (error) {
        console.error('Error loading redirect links:', error);
      }
    };

    loadAllRedirectLinks();
  }, [project, setRedirectLinksFromData]);

  // Redirect effect - check if redirect is enabled and redirect URL exists
  useEffect(() => {
    // Check if we're in an iframe - if so, don't redirect
    const isInIframe = window !== window.top;
    
    console.log(`🔍 Redirect check for ${bookid}:`, {
      redirectEnabled,
      redirectLinksCount: Object.keys(redirectLinks || {}).length,
      redirectLinks,
      bookid,
      isInIframe
    });
    
    // Don't redirect if we're in an iframe (mobile infobox scenario)
    if (isInIframe) {
      console.log(`📱 In iframe, skipping redirect`);
      return;
    }
    
    // More robust redirection logic that works better on mobile
    if (bookid && redirectEnabled && redirectLinks && Object.keys(redirectLinks).length > 0) {
      
      // Check if URL has language parameters - if so, skip redirection
      const urlParams = new URLSearchParams(window.location.search);
      const hasLanguageParam = urlParams.has('lang');
      
      if (hasLanguageParam) {
        // Don't redirect if language parameter is present
        console.log(`🌐 Language parameter detected, skipping redirect`);
        return;
      }
      
      const redirectUrl = getRedirectUrl(project, bookid);
      console.log(`🔗 Redirect URL for ${bookid}:`, redirectUrl);
      if (redirectUrl) {
        console.log(`🚀 Redirecting to: ${redirectUrl}`);
        
        // Use a more reliable redirection method for mobile devices
        try {
          if (isMobileDevice()) {
            // For mobile devices, use replace to avoid back button issues
            console.log(`📱 Mobile redirect using replace`);
            window.location.replace(redirectUrl);
          } else {
            // For desktop, use href
            console.log(`🖥️ Desktop redirect using href`);
            window.location.href = redirectUrl;
          }
        } catch (error) {
          // Fallback to window.location.href
          console.log(`⚠️ Redirect error, using fallback:`, error);
          window.location.href = redirectUrl;
        }
      } else {
        console.log(`❌ No redirect URL found for ${bookid}`);
      }
    }
  }, [bookid, project, redirectEnabled, redirectLinks, getRedirectUrl, isMobileDevice]);

  // Additional effect to handle redirection after data loads (fallback)
  useEffect(() => {
    // Check if we're in an iframe - if so, don't redirect
    const isInIframe = window !== window.top;
    
    if (isInIframe) {
      console.log(`📱 In iframe (fallback check), skipping redirect`);
      return;
    }
    
    if (bookid && !loading && !error && bookData && redirectEnabled && redirectLinks) {
      // Check if URL has language parameters - if so, skip redirection
      const urlParams = new URLSearchParams(window.location.search);
      const hasLanguageParam = urlParams.has('lang');
      
      if (hasLanguageParam) {
        // Don't redirect if language parameter is present
        return;
      }
      
      const redirectUrl = getRedirectUrl(project, bookid);
      if (redirectUrl && window.location.href.includes(bookid)) {
        // Small delay to ensure page is ready
        setTimeout(() => {
          if (isMobileDevice()) {
            window.location.replace(redirectUrl);
          } else {
            window.location.href = redirectUrl;
          }
        }, 100);
      }
    }
  }, [bookid, project, loading, error, bookData, redirectEnabled, redirectLinks, getRedirectUrl, isMobileDevice]);

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    const headers = lines[0].split(',');
    const items = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line) {
        const values = parseCSVLine(line);
        if (values.length > 0) {
          const item = {};
          headers.forEach((header, index) => {
            item[header.trim()] = values[index] ? values[index].trim() : '';
          });
          // Only add items that have a code
          if (item.code && item.code.trim()) {
            items.push(item);
          }
        }
      }
    }
    return items;
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    
    // Clean up the results - remove quotes and trim
    return result.map(field => {
      let cleaned = field.trim();
      if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
        cleaned = cleaned.slice(1, -1);
      }
      return cleaned;
    });
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const getLocalizedValue = (field, data) => {
    const field_name = `${field}_${lang}`;
    if (data[field_name] === "-" || !data[field_name]) {
      return lang === "en" ? data[`${field}_tc`] : data[`${field}_en`]
    } else {
      return data[field_name]
    }
  };

  const getLocalizedType = (data) => {
    // For English (default), use base 'type' column first
    if (lang === "en") {
      if (data.type && data.type !== "-" && data.type.trim()) {
        return data.type;
      } else if (data.type_en && data.type_en !== "-" && data.type_en.trim()) {
        return data.type_en;
      } else if (data.type_tc && data.type_tc !== "-" && data.type_tc.trim()) {
        return data.type_tc;
      } else if (data.type_sc && data.type_sc !== "-" && data.type_sc.trim()) {
        return data.type_sc;
      }
    } else {
      // For other languages, use language-specific column first
      const type_field = `type_${lang}`;
      if (data[type_field] && data[type_field] !== "-" && data[type_field].trim()) {
        return data[type_field];
      } else if (data.type && data.type !== "-" && data.type.trim()) {
        return data.type;
      } else if (lang === "tc" && data.type_en && data.type_en !== "-" && data.type_en.trim()) {
        return data.type_en;
      } else if (lang === "sc" && data.type_en && data.type_en !== "-" && data.type_en.trim()) {
        return data.type_en;
      }
    }
    return null;
  };

  const getLocalizedCategory = (data) => {
    // For English (default), use base 'category' column first
    if (lang === "en") {
      if (data.category && data.category !== "-" && data.category.trim()) {
        return data.category;
      } else if (data.category_en && data.category_en !== "-" && data.category_en.trim()) {
        return data.category_en;
      } else if (data.category_tc && data.category_tc !== "-" && data.category_tc.trim()) {
        return data.category_tc;
      } else if (data.category_sc && data.category_sc !== "-" && data.category_sc.trim()) {
        return data.category_sc;
      }
    } else {
      // For other languages, use language-specific column first
      const category_field = `category_${lang}`;
      if (data[category_field] && data[category_field] !== "-" && data[category_field].trim()) {
        return data[category_field];
      } else if (data.category && data.category !== "-" && data.category.trim()) {
        return data.category;
      } else if (lang === "tc" && data.category_en && data.category_en !== "-" && data.category_en.trim()) {
        return data.category_en;
      } else if (lang === "sc" && data.category_en && data.category_en !== "-" && data.category_en.trim()) {
        return data.category_en;
      }
    }
    return null;
  };

  const getCompanyType = (bookCode) => {
    if (!bookCode) return '';
    const bCount = (bookCode.match(/B/g) || []).length;
    const pCount = (bookCode.match(/P/g) || []).length;
    
    if (bCount === 1) return 'Publisher';
    if (pCount === 1) return 'Printer';
    return '';
  };

  const getAddress = (data) => {
    if (data.address_tc && data.address_tc !== '-') return data.address_tc;
    if (data.address_sc && data.address_sc !== '-') return data.address_sc;
    if (data.address_en && data.address_en !== '-') return data.address_en;
    return '-';
  };

  // Convert Excel serial number to readable date (fallback for existing data)
  const convertExcelDate = (excelSerial) => {
    if (!excelSerial || isNaN(excelSerial)) return excelSerial;
    
    // Excel serial number to JavaScript date conversion
    // Excel counts days from 1900-01-01, but has a leap year bug
    const excelEpoch = new Date(1900, 0, 1);
    const jsDate = new Date(excelEpoch.getTime() + (excelSerial - 2) * 24 * 60 * 60 * 1000);
    
    // Format as readable date
    return jsDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Check if a value is an Excel serial date
  const isExcelSerialDate = (value) => {
    if (!value || isNaN(value)) return false;
    const num = parseFloat(value);
    // Excel serial dates are typically between 1 (1900-01-01) and 50000 (2036-12-31)
    return num >= 1 && num <= 50000;
  };

  // Format date to the new format (1-Jul-2024)
  const formatDateToNewFormat = (dateValue) => {
    if (!dateValue) return dateValue;
    
    // If it's already in the new format (contains month abbreviation), return as is
    if (typeof dateValue === 'string' && /^\d{1,2}-[A-Za-z]{3}-\d{4}$/.test(dateValue.trim())) {
      return dateValue.trim();
    }
    
    // If it's an Excel serial number, convert it
    if (isExcelSerialDate(dateValue)) {
      const convertedDate = convertExcelDate(dateValue);
      return convertToNewFormat(convertedDate);
    }
    
    // If it's already a readable date, convert to new format
    if (typeof dateValue === 'string' && dateValue.includes('/')) {
      return convertToNewFormat(dateValue);
    }
    
    return dateValue;
  };

  // Convert date string to new format (1-Jul-2024)
  const convertToNewFormat = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      const day = date.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString;
    }
  };

  // Get formatted date value
  const getFormattedDate = (dateValue) => {
    return formatDateToNewFormat(dateValue);
  };


  if (loading) {
    return (
      <div className="loading-container">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: 'auto', height: 'auto' }}>
      <audio hidden id='audioPlayer' controls>
        <source src={''} type="audio/mpeg" />
      </audio>
      {/* Mobile Logo - Top of Page */}
      <div className="mobile-logo">
        <img src={logoUrl} alt={logoAlt} />
      </div>
      
      
      {/* Desktop Logos - Top of Page, Spanning Both Columns */}
      <div className="desktop-logos">
        <div className="desktop-logo">
          <img src={logoUrl} alt={logoAlt} />
        </div>
      </div>
      
      <div className="desktop-layout-container">
        {/* Desktop Layout - Left Side (Media) and Right Side (Info) */}
        <div className="desktop-media-section">
          {mediaType === "glb" && (
            <ARGlbViewer glbPath={getGLBPath(bookid, project)} viewerRef={viewerRef} />
          )}
          
          {mediaType === "image" && (
            <ImageViewer imagePath={`${project.toUpperCase()}/covers/${bookid}.webp`} />
          )}
          
          {companyData && (
            <ImageViewer imagePath={`company_poster/${companyid}.webp`} />
          )}
          
          {mediaType === "none" && bookData && (
            <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg w-full max-w-md">
              <div className="text-center">
                <div className="text-gray-500 text-lg mb-2">No Media Available</div>
                <div className="text-gray-400 text-sm">Book cover or 3D model not found</div>
              </div>
            </div>
          )}
        </div>
        
        {/* Desktop Layout - Right Side - Book Information */}
        <div className="desktop-info-section">
          {bookData && (
            <div className="infobox-table">
              {/* Desktop Title Section */}
              <div className="desktop-title-section">
                <div className="desktop-title-text">{getLocalizedValue("name", bookData) || 'Untitled'}</div>
                {getLocalizedValue("publisher", bookData) && (
                  <div className="desktop-publisher-text">{getLocalizedValue("publisher", bookData)}</div>
                )}
              </div>
              
              {/* Desktop tabs */}
              <div className="desktop-tabs">
                <button 
                  className={`desktop-tab ${activeTab === 'description' ? 'active' : 'inactive'}`}
                  onClick={() => setActiveTab('description')}
                >
                  Product Description
                </button>
                <button 
                  className={`desktop-tab ${activeTab === 'contact' ? 'active' : 'inactive'}`}
                  onClick={() => setActiveTab('contact')}
                >
                  Contact Us
                </button>
              </div>
              
              {/* Product Description Tab Content */}
              <div className={`desktop-tab-content ${activeTab === 'description' ? 'active' : ''}`}>
                {getLocalizedValue("author", bookData) && (
                  <div className="table-row">
                    <div className="table-cell label">{t("author")}</div>
                    <div className="table-cell value">{getLocalizedValue("author", bookData)}</div>
                  </div>
                )}
                
                {getLocalizedValue("introduction", bookData) && (
                  <div className="table-row">
                    <div className="table-cell label">
                      <div className="flex gap-3 items-center">
                        <span>{t("introduction")}</span>
                        {(usePreGeneratedAudio ? audioUrl : true) && (
                          <img
                            alt={isAudioPlaying ? "pause" : "sound"}
                            className="cursor-pointer h-[2vh] max-h-5 min-h-3"
                            src={volume}
                            onClick={() => {
                              const text = bookData[`introduction_${lang}`] || getLocalizedValue('introduction', bookData);
                              toggleAudio(text);
                            }}
                          />
                        )}
                      </div>
                    </div>
                    <div className="table-cell value">{getLocalizedValue("introduction", bookData)}</div>
                  </div>
                )}
                
                {bookData.isbn && (
                  <div className="table-row">
                    <div className="table-cell label">{t("isbn")}</div>
                    <div className="table-cell value">{bookData.isbn}</div>
                  </div>
                )}
                
                {bookData.publication_date && (
                  <div className="table-row">
                    <div className="table-cell label">{t("publication_date")}</div>
                    <div className="table-cell value">{getFormattedDate(bookData.publication_date)}</div>
                  </div>
                )}
                
                {getLocalizedType(bookData) && (
                  <div className="table-row">
                    <div className="table-cell label">{t("type")}</div>
                    <div className="table-cell value">{getLocalizedType(bookData)}</div>
                  </div>
                )}
                
                {getLocalizedCategory(bookData) && (
                  <div className="table-row">
                    <div className="table-cell label">{t("category")}</div>
                    <div className="table-cell value">{getLocalizedCategory(bookData)}</div>
                  </div>
                )}
                
                
                {/* Contact Us Button - Inside Product Description tab */}
                {isValidEmail(bookData?.copyright_contact_person_email) && (
                  <div style={{ marginTop: '16px', marginBottom: '30px', paddingBottom: '20px' }}>
                    <a 
                      href={`mailto:${bookData.copyright_contact_person_email}`} 
                      className="button primary"
                    >
                      Contact Us
                    </a>
                  </div>
                )}
              </div>
              
              {/* Contact Us Tab Content */}
              <div className={`desktop-tab-content ${activeTab === 'contact' ? 'active' : ''}`}>
                {getLocalizedValue("publisher", bookData) && (
                  <div className="table-row">
                    <div className="table-cell label">{t("publisher")}</div>
                    <div className="table-cell value">{getLocalizedValue("publisher", bookData)}</div>
                  </div>
                )}
                
                {bookData.code && (
                  <div className="table-row">
                    <div className="table-cell label">{t("type_of_company")}</div>
                    <div className="table-cell value">{getCompanyType(bookData.code)}</div>
                  </div>
                )}
                
                {getLocalizedValue("copyright_contact_person_name", bookData) && (
                  <div className="table-row">
                    <div className="table-cell label">{t("copyright_contact_person_name")}</div>
                    <div className="table-cell value">{getLocalizedValue("copyright_contact_person_name", bookData)}</div>
                  </div>
                )}
                
                {getLocalizedValue("copyright_contact_position", bookData) && (
                  <div className="table-row">
                    <div className="table-cell label">{t("copyright_contact_positon")}</div>
                    <div className="table-cell value">{getLocalizedValue("copyright_contact_position", bookData)}</div>
                  </div>
                )}
                
                {bookData.copyright_contact_person_email && (
                  <div className="table-row">
                    <div className="table-cell label">{t("copyright_contact_person_email")}</div>
                    <div className="table-cell value">{bookData.copyright_contact_person_email}</div>
                  </div>
                )}
                
                {bookData.copyright_contact_person_phone && (
                  <div className="table-row">
                    <div className="table-cell label">{t("copyright_contact_person_phone")}</div>
                    <div className="table-cell value">{bookData.copyright_contact_person_phone}</div>
                  </div>
                )}
                
                {bookData.copyright_contact_person_fax && (
                  <div className="table-row">
                    <div className="table-cell label">{t("copyright_contact_person_fax")}</div>
                    <div className="table-cell value">{bookData.copyright_contact_person_fax}</div>
                  </div>
                )}
                
                <div className="table-row">
                  <div className="table-cell label">{t("address")}</div>
                  <div className="table-cell value">{getAddress(bookData)}</div>
                </div>
                
                {bookData.website && (
                  <div className="table-row">
                    <div className="table-cell label">{t("website")}</div>
                    <div className="table-cell value">
                      <a
                        className="underline text-blue-600 hover:text-blue-800 visited:text-purple-600"
                        href={
                          bookData.website.startsWith("http")
                            ? bookData.website
                            : `https://${bookData.website}`
                        }
                        target="_blank"
                        rel="noreferrer"
                      >
                        {bookData.website}
                      </a>
                    </div>
                  </div>
                )}
                
                {/* Contact Us Button - Inside Contact Us tab */}
                {isValidEmail(bookData?.copyright_contact_person_email) && (
                  <div style={{ marginTop: '16px', marginBottom: '30px', paddingBottom: '20px' }}>
                    <a 
                      href={`mailto:${bookData.copyright_contact_person_email}`} 
                      className="button primary"
                    >
                      Contact Us
                    </a>
                  </div>
                )}
              </div>
              
            </div>
          )}
          
          {companyData && (
            <div className="infobox-table">
              {/* Company Information */}
              <div className="table-row">
                <div className="table-cell label">Name</div>
                <div className="table-cell value">{getLocalizedValue("exhibitor", companyData) || 'Untitled Company'}</div>
              </div>
              
              {getLocalizedValue("contact_person_name", companyData) && (
                <div className="table-row">
                  <div className="table-cell label">Contact Person</div>
                  <div className="table-cell value">{getLocalizedValue("contact_person_name", companyData)}</div>
                </div>
              )}
              
              {getLocalizedValue("contact_person_position", companyData) && (
                <div className="table-row">
                  <div className="table-cell label">Contact Person's Position</div>
                  <div className="table-cell value">{getLocalizedValue("contact_person_position", companyData)}</div>
                </div>
              )}
              
              {companyData.contact_person_email && (
                <div className="table-row">
                  <div className="table-cell label">Contact Person's Email</div>
                  <div className="table-cell value">{companyData.contact_person_email}</div>
                </div>
              )}
              
              {companyData.contact_person_phone && (
                <div className="table-row">
                  <div className="table-cell label">Contact Person's Phone</div>
                  <div className="table-cell value">{companyData.contact_person_phone}</div>
                </div>
              )}
              
              {companyData.contact_person_fax && (
                <div className="table-row">
                  <div className="table-cell label">Fax</div>
                  <div className="table-cell value">{companyData.contact_person_fax}</div>
                </div>
              )}
              
              {companyData.company_website && (
                <div className="table-row">
                  <div className="table-cell label">Website</div>
                  <div className="table-cell value">{companyData.company_website}</div>
                </div>
              )}
              
              {companyData.code && (
                <div className="table-row">
                  <div className="table-cell label">Code</div>
                  <div className="table-cell value">{companyData.code}</div>
                </div>
              )}
              
              {/* Contact Us Button */}
              {isValidEmail(companyData?.contact_person_email) && (
                <div style={{ marginTop: '16px', marginBottom: '30px', paddingBottom: '20px' }}>
                  <a 
                    href={`mailto:${companyData.contact_person_email}`} 
                    className="button primary"
                  >
                    Contact Us
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Mobile Layout - Top (Media) and Bottom (Info) */}
      <div className="mobile-layout-container">
        {/* Mobile Media Section - Top */}
        <div className="mobile-media-section">
          {mediaType === "glb" && (
            <ARGlbViewer glbPath={getGLBPath(bookid, project)} viewerRef={viewerRef} />
          )}
          
          {mediaType === "image" && (
            <ImageViewer imagePath={`${project.toUpperCase()}/covers/${bookid}.webp`} />
          )}
          
          {companyData && (
            <ImageViewer imagePath={`company_poster/${companyid}.webp`} />
          )}
          
          {mediaType === "none" && bookData && (
            <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg w-full max-w-md">
              <div className="text-center">
                <div className="text-gray-500 text-lg mb-2">No Media Available</div>
                <div className="text-gray-400 text-sm">Book cover or 3D model not found</div>
              </div>
            </div>
          )}
        </div>
        
        {/* Mobile Info Section - Bottom */}
        <div className="mobile-info-section">
          {bookData && (
            <div className="infobox-table">
              {/* Basic Book Information */}
              <div className="table-row">
                <div className="table-cell label">{t("name")}</div>
                <div className="table-cell value">{getLocalizedValue("name", bookData) || 'Untitled'}</div>
              </div>
              
              {/* Mobile-only title row that spans both columns */}
              <div className="table-row mobile-title-row">
                <div className="table-cell mobile-title">
                  <div className="mobile-title-text">{getLocalizedValue("name", bookData) || 'Untitled'}</div>
                  {getLocalizedValue("publisher", bookData) && (
                    <div 
                      className="mobile-publisher-text"
                      style={{
                        fontWeight: 'bold !important',
                        fontSize: 'max(18px, min(20px, 1.6vw)) !important',
                        fontFamily: 'inherit !important',
                        WebkitFontSmoothing: 'antialiased !important',
                        MozOsxFontSmoothing: 'grayscale !important',
                        textRendering: 'optimizeLegibility !important'
                      }}
                    >
                      {getLocalizedValue("publisher", bookData)}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Mobile tabs */}
              <div className="mobile-tabs">
                <button 
                  className={`mobile-tab ${activeTab === 'description' ? 'active' : 'inactive'}`}
                  onClick={() => setActiveTab('description')}
                >
                  Product Description
                </button>
                <button 
                  className={`mobile-tab ${activeTab === 'contact' ? 'active' : 'inactive'}`}
                  onClick={() => setActiveTab('contact')}
                >
                  Contact Us
                </button>
              </div>
              
              {/* Product Description Tab Content */}
              <div className={`mobile-tab-content ${activeTab === 'description' ? 'active' : ''}`}>
                {getLocalizedValue("author", bookData) && (
                  <div className="table-row">
                    <div className="table-cell label">{t("author")}</div>
                    <div className="table-cell value">{getLocalizedValue("author", bookData)}</div>
                  </div>
                )}
                
                {getLocalizedValue("introduction", bookData) && (
                  <div className="table-row">
                    <div className="table-cell label">
                      <div className="flex gap-3 items-center">
                        <span>{t("introduction")}</span>
                        {(usePreGeneratedAudio ? audioUrl : true) && (
                          <img
                            alt={isAudioPlaying ? "pause" : "sound"}
                            className="cursor-pointer h-[2vh] max-h-5 min-h-3"
                            src={volume}
                            onClick={() => {
                              const text = bookData[`introduction_${lang}`] || getLocalizedValue('introduction', bookData);
                              toggleAudio(text);
                            }}
                          />
                        )}
                      </div>
                    </div>
                    <div className="table-cell value">{getLocalizedValue("introduction", bookData)}</div>
                  </div>
                )}
                
                {bookData.isbn && (
                  <div className="table-row">
                    <div className="table-cell label">{t("isbn")}</div>
                    <div className="table-cell value">{bookData.isbn}</div>
                  </div>
                )}
                
                {bookData.publication_date && (
                  <div className="table-row">
                    <div className="table-cell label">{t("publication_date")}</div>
                    <div className="table-cell value">{getFormattedDate(bookData.publication_date)}</div>
                  </div>
                )}
                
                {getLocalizedType(bookData) && (
                  <div className="table-row">
                    <div className="table-cell label">{t("type")}</div>
                    <div className="table-cell value">{getLocalizedType(bookData)}</div>
                  </div>
                )}
                
                {getLocalizedCategory(bookData) && (
                  <div className="table-row">
                    <div className="table-cell label">{t("category")}</div>
                    <div className="table-cell value">{getLocalizedCategory(bookData)}</div>
                  </div>
                )}
                
                
                {/* Contact Us Button - Inside Product Description tab */}
                {isValidEmail(bookData?.copyright_contact_person_email) && (
                  <div style={{ marginTop: '16px', marginBottom: '30px', paddingBottom: '20px' }}>
                    <a 
                      href={`mailto:${bookData.copyright_contact_person_email}`} 
                      className="button primary"
                    >
                      Contact Us
                    </a>
                  </div>
                )}
              </div>
              
              {/* Contact Us Tab Content */}
              <div className={`mobile-tab-content ${activeTab === 'contact' ? 'active' : ''}`}>
                {getLocalizedValue("publisher", bookData) && (
                  <div className="table-row">
                    <div className="table-cell label">{t("publisher")}</div>
                    <div className="table-cell value">{getLocalizedValue("publisher", bookData)}</div>
                  </div>
                )}
                
                {bookData.code && (
                  <div className="table-row">
                    <div className="table-cell label">{t("type_of_company")}</div>
                    <div className="table-cell value">{getCompanyType(bookData.code)}</div>
                  </div>
                )}
                
                {getLocalizedValue("copyright_contact_person_name", bookData) && (
                  <div className="table-row">
                    <div className="table-cell label">{t("copyright_contact_person_name")}</div>
                    <div className="table-cell value">{getLocalizedValue("copyright_contact_person_name", bookData)}</div>
                  </div>
                )}
                
                {getLocalizedValue("copyright_contact_position", bookData) && (
                  <div className="table-row">
                    <div className="table-cell label">{t("copyright_contact_positon")}</div>
                    <div className="table-cell value">{getLocalizedValue("copyright_contact_position", bookData)}</div>
                  </div>
                )}
                
                {bookData.copyright_contact_person_email && (
                  <div className="table-row">
                    <div className="table-cell label">{t("copyright_contact_person_email")}</div>
                    <div className="table-cell value">{bookData.copyright_contact_person_email}</div>
                  </div>
                )}
                
                {bookData.copyright_contact_person_phone && (
                  <div className="table-row">
                    <div className="table-cell label">{t("copyright_contact_person_phone")}</div>
                    <div className="table-cell value">{bookData.copyright_contact_person_phone}</div>
                  </div>
                )}
                
                {bookData.copyright_contact_person_fax && (
                  <div className="table-row">
                    <div className="table-cell label">{t("copyright_contact_person_fax")}</div>
                    <div className="table-cell value">{bookData.copyright_contact_person_fax}</div>
                  </div>
                )}
                
                <div className="table-row">
                  <div className="table-cell label">{t("address")}</div>
                  <div className="table-cell value">{getAddress(bookData)}</div>
                </div>
                
                {bookData.website && (
                  <div className="table-row">
                    <div className="table-cell label">{t("website")}</div>
                    <div className="table-cell value">
                      <a
                        className="underline text-blue-600 hover:text-blue-800 visited:text-purple-600"
                        href={
                          bookData.website.startsWith("http")
                            ? bookData.website
                            : `https://${bookData.website}`
                        }
                        target="_blank"
                        rel="noreferrer"
                      >
                        {bookData.website}
                      </a>
                    </div>
                  </div>
                )}
              
                {/* Contact Us Button - Inside Contact Us tab */}
              {isValidEmail(bookData?.copyright_contact_person_email) && (
                <div style={{ marginTop: '16px', marginBottom: '30px', paddingBottom: '20px' }}>
                  <a 
                    href={`mailto:${bookData.copyright_contact_person_email}`} 
                    className="button primary"
                  >
                    Contact Us
                  </a>
                </div>
              )}
              </div>
              
            </div>
          )}
          
          {companyData && (
            <div className="infobox-table">
              {/* Company Information */}
              <div className="table-row">
                <div className="table-cell label">Name</div>
                <div className="table-cell value">{getLocalizedValue("exhibitor", companyData) || 'Untitled Company'}</div>
              </div>
              
              {getLocalizedValue("contact_person_name", companyData) && (
                <div className="table-row">
                  <div className="table-cell label">Contact Person</div>
                  <div className="table-cell value">{getLocalizedValue("contact_person_name", companyData)}</div>
                </div>
              )}
              
              {getLocalizedValue("contact_person_position", companyData) && (
                <div className="table-row">
                  <div className="table-cell label">Contact Person's Position</div>
                  <div className="table-cell value">{getLocalizedValue("contact_person_position", companyData)}</div>
                </div>
              )}
              
              {companyData.contact_person_email && (
                <div className="table-row">
                  <div className="table-cell label">Contact Person's Email</div>
                  <div className="table-cell value">{companyData.contact_person_email}</div>
                </div>
              )}
              
              {companyData.contact_person_phone && (
                <div className="table-row">
                  <div className="table-cell label">Contact Person's Phone</div>
                  <div className="table-cell value">{companyData.contact_person_phone}</div>
                </div>
              )}
              
              {companyData.contact_person_fax && (
                <div className="table-row">
                  <div className="table-cell label">Fax</div>
                  <div className="table-cell value">{companyData.contact_person_fax}</div>
                </div>
              )}
              
              {companyData.company_website && (
                <div className="table-row">
                  <div className="table-cell label">Website</div>
                  <div className="table-cell value">{companyData.company_website}</div>
                </div>
              )}
              
              {companyData.code && (
                <div className="table-row">
                  <div className="table-cell label">Code</div>
                  <div className="table-cell value">{companyData.code}</div>
                </div>
              )}
              
              {/* Contact Us Button */}
              {isValidEmail(companyData?.contact_person_email) && (
                <div style={{ marginTop: '16px', marginBottom: '30px', paddingBottom: '20px' }}>
                  <a 
                    href={`mailto:${companyData.contact_person_email}`} 
                    className="button primary"
                  >
                    Contact Us
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CSVBookDetail;
